import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';

interface Props {
  onBack: () => void;
  onSave?: () => void;
}

export const ForeignQuotaDocSettingsView: React.FC<Props> = ({ onBack, onSave }) => {
  const [subject, setSubject] = useState('แจ้งรายชื่อ และอัตราส่วนกรรมสิทธิ์ชาวต่างชาติ');
  const [toWhom, setToWhom] = useState('เจ้าพนักงานที่ดินกรุงเทพมหานคร');
  const [para1, setPara1] = useState(
    'เนื่องจาก นิติบุคคลหมู่บ้านจัดสรร เอส วี เอ็ม ซัพพอร์ต มีเนื้อที่ทั้งหมด 50,703.14 ตารางวา และ ชาวต่างชาติสามารถถือกรรมสิทธิ์ในห้องชุดได้ เมื่อรวมกันแล้วต้องไม่เกินอัตราร้อยละ 49 ของพื้นที่ทั้งหมด คือ 24,844.54 ตารางวา'
  );
  const [para2, setPara2] = useState(
    'จะมีการโอนกรรมสิทธิ์ห้องชุดให้แก่บุคคลต่างชาติ ซึ่งขอแจ้งอัตราส่วนพื้นที่ของบุคคลต่างชาติดังต่อไปนี้'
  );
  const [note, setNote] = useState('');
  const [customSigner, setCustomSigner] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [signerName, setSignerName] = useState('นายศศิธร สุขสมบูรณ์');
  const [customSignerInput, setCustomSignerInput] = useState('');
  const [signerPosition, setSignerPosition] = useState('ผู้จัดการนิติบุคคลอาคารชุด / หมู่บ้านจัดสรร');

  const rows = [
    { id: 1, unit: '100/1', owner: 'Liam', nation: 'USA', area: '27.00', percent: '0.77%', status: 'อยู่อาศัยเอง' },
    { id: 2, unit: '100/2', owner: 'John', nation: 'USA', area: '27.00', percent: '0.77%', status: 'อยู่อาศัยเอง' },
    { id: 3, unit: '100/3', owner: 'Roger', nation: 'USA', area: '27.00', percent: '0.77%', status: 'อยู่อาศัยเอง' },
    { id: 4, unit: '100/4', owner: 'Yu Yin', nation: 'CN', area: '27.00', percent: '0.77%', status: 'อยู่อาศัยเอง' },
    { id: 5, unit: '100/5', owner: 'Kurumi', nation: 'JP', area: '27.00', percent: '0.77%', status: 'อยู่อาศัยเอง' },
  ];

  const handleSave = () => {
    if (onSave) onSave();
  };

  const displaySigner = customSigner ? (customSignerInput || '...................................................') : signerName;

  return (
    <div className="bg-white min-h-[600px] rounded-xl p-6 sm:p-8 font-prompt space-y-6">
      {/* Top Header */}
      <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
        <button
          type="button"
          onClick={onBack}
          className="w-8 h-8 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
          title="ย้อนกลับ"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
          ตั้งค่าเอกสารกรรมสิทธิ์ชาวต่างชาติ
        </h1>
      </div>

      {/* 2-Column Split: Form (Left) & Document Preview (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: Form Controls */}
        <div className="lg:col-span-5 space-y-5">
          {/* เรื่อง */}
          <div className="space-y-1">
            <label className="text-xs sm:text-sm font-bold text-slate-800 block">เรื่อง</label>
            <input
              type="text"
              maxLength={100}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full p-2.5 text-xs sm:text-sm border border-slate-300 rounded-md bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <div className="text-right">
              <span className="text-[11px] text-rose-500 font-mono">{subject.length} / 100</span>
            </div>
          </div>

          {/* เรียน */}
          <div className="space-y-1">
            <label className="text-xs sm:text-sm font-bold text-slate-800 block">เรียน</label>
            <input
              type="text"
              maxLength={100}
              value={toWhom}
              onChange={(e) => setToWhom(e.target.value)}
              className="w-full p-2.5 text-xs sm:text-sm border border-slate-300 rounded-md bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <div className="text-right">
              <span className="text-[11px] text-rose-500 font-mono">{toWhom.length} / 100</span>
            </div>
          </div>

          {/* คำอธิบายเอกสาร ย่อหน้าที่ 1 */}
          <div className="space-y-1">
            <label className="text-xs sm:text-sm font-bold text-slate-800 block">
              คำอธิบายเอกสาร ย่อหน้าที่ 1
            </label>
            <textarea
              rows={4}
              maxLength={800}
              value={para1}
              onChange={(e) => setPara1(e.target.value)}
              className="w-full p-2.5 text-xs sm:text-sm border border-slate-300 rounded-md bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 leading-relaxed"
            />
            <div className="text-right">
              <span className="text-[11px] text-rose-500 font-mono">{para1.length} / 800</span>
            </div>
          </div>

          {/* คำอธิบายเอกสาร ย่อหน้าที่ 2 */}
          <div className="space-y-1">
            <label className="text-xs sm:text-sm font-bold text-slate-800 block">
              คำอธิบายเอกสาร ย่อหน้าที่ 2
            </label>
            <textarea
              rows={3}
              maxLength={800}
              value={para2}
              onChange={(e) => setPara2(e.target.value)}
              className="w-full p-2.5 text-xs sm:text-sm border border-slate-300 rounded-md bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 leading-relaxed"
            />
            <div className="text-right">
              <span className="text-[11px] text-rose-500 font-mono">{para2.length} / 800</span>
            </div>
          </div>

          {/* หมายเหตุ */}
          <div className="space-y-1">
            <label className="text-xs sm:text-sm font-bold text-slate-800 block">หมายเหตุ</label>
            <textarea
              rows={3}
              maxLength={254}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="ระบุหมายเหตุเพิ่มเติม (ถ้ามี)"
              className="w-full p-2.5 text-xs sm:text-sm border border-slate-300 rounded-md bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <div className="text-right">
              <span className="text-[11px] text-rose-500 font-mono">{note.length} / 254</span>
            </div>
          </div>

          {/* ผู้ลงนาม checkboxes */}
          <div className="space-y-3 pt-1">
            <label className="text-xs sm:text-sm font-bold text-slate-800 block">ผู้ลงนาม</label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer text-xs sm:text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={customSigner}
                  onChange={(e) => setCustomSigner(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500 cursor-pointer"
                />
                <span>ระบุเอง</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer text-xs sm:text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={showSignature}
                  onChange={(e) => setShowSignature(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500 cursor-pointer"
                />
                <span>แสดงลายเซ็น</span>
              </label>
            </div>

            {/* ชื่อผู้ลงนาม */}
            <div className="space-y-1 pt-1">
              <label className="text-xs font-semibold text-slate-700 block">ชื่อผู้ลงนาม</label>
              {customSigner ? (
                <input
                  type="text"
                  value={customSignerInput}
                  onChange={(e) => setCustomSignerInput(e.target.value)}
                  placeholder="พิมพ์ชื่อ-นามสกุล ผู้ลงนาม"
                  className="w-full p-2.5 text-xs sm:text-sm border border-slate-300 rounded-md bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              ) : (
                <select
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  className="w-full p-2.5 text-xs sm:text-sm border border-slate-300 rounded-md bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                >
                  <option value="นายศศิธร สุขสมบูรณ์">นายศศิธร สุขสมบูรณ์ (ผู้จัดการนิติบุคคล)</option>
                  <option value="นางสาววิภาดา สถิตย์">นางสาววิภาดา สถิตย์ (ประธานกรรมการ)</option>
                  <option value="นายสมศักดิ์ มั่งมี">นายสมศักดิ์ มั่งมี (กรรมการฝ่ายกฎหมาย)</option>
                </select>
              )}
            </div>

            {/* ตำแหน่งผู้ลงนาม */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700 block">ตำแหน่งผู้ลงนาม</label>
              <input
                type="text"
                value={signerPosition}
                onChange={(e) => setSignerPosition(e.target.value)}
                className="w-full p-2.5 text-xs sm:text-sm border border-slate-200 rounded-md bg-slate-100 text-slate-700 focus:outline-none"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4">
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-md text-sm font-semibold transition-colors cursor-pointer"
            >
              ยกเลิก
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-6 py-2.5 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-sm font-semibold transition-colors shadow-xs cursor-pointer"
            >
              บันทึก
            </button>
          </div>
        </div>

        {/* Right Column: Live Document Preview (A4 Card) */}
        <div className="lg:col-span-7 bg-white border border-slate-300 rounded-xl p-8 sm:p-10 shadow-sm text-slate-900 text-xs sm:text-sm space-y-6 font-prompt">
          {/* Letterhead Header */}
          <div className="text-center space-y-1">
            <h3 className="font-bold text-sm sm:text-base text-slate-900">
              นิติบุคคลหมู่บ้านจัดสรร เอส วี เอ็ม ซัพพอร์ต
            </h3>
          </div>

          {/* Date */}
          <div className="text-right text-xs text-slate-700 font-medium">
            วันที่ 29 มี.ค. 2567
          </div>

          {/* Subject & To */}
          <div className="space-y-1.5 text-xs sm:text-sm">
            <div className="flex gap-4">
              <span className="font-bold shrink-0 w-12">เรื่อง</span>
              <span className="text-slate-800">{subject}</span>
            </div>
            <div className="flex gap-4">
              <span className="font-bold shrink-0 w-12">เรียน</span>
              <span className="text-slate-800">{toWhom}</span>
            </div>
          </div>

          {/* Paragraph 1 */}
          <div className="text-xs sm:text-sm leading-relaxed text-slate-800 text-justify">
            <span className="inline-block w-8" />
            {para1}
          </div>

          {/* Paragraph 2 */}
          <div className="text-xs sm:text-sm leading-relaxed text-slate-800 text-justify">
            <span className="inline-block w-8" />
            {para2}
          </div>

          {/* Quota Table */}
          <div className="border border-slate-400 rounded-xs overflow-hidden">
            <table className="w-full text-center text-[11px] sm:text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-400 bg-slate-50 font-semibold text-slate-800">
                  <th className="p-2 border-r border-slate-400">ลำดับ</th>
                  <th className="p-2 border-r border-slate-400">บ้านเลขที่</th>
                  <th className="p-2 border-r border-slate-400">เจ้าของกรรมสิทธิ์</th>
                  <th className="p-2 border-r border-slate-400">สัญชาติ</th>
                  <th className="p-2 border-r border-slate-400">พื้นที่ (ตร.ม.)</th>
                  <th className="p-2 border-r border-slate-400">%</th>
                  <th className="p-2">สถานะ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-300">
                {rows.map((r) => (
                  <tr key={r.id} className="text-slate-700">
                    <td className="p-2 border-r border-slate-300">{r.id}</td>
                    <td className="p-2 border-r border-slate-300">{r.unit}</td>
                    <td className="p-2 border-r border-slate-300 font-medium">{r.owner}</td>
                    <td className="p-2 border-r border-slate-300">{r.nation}</td>
                    <td className="p-2 border-r border-slate-300">{r.area}</td>
                    <td className="p-2 border-r border-slate-300">{r.percent}</td>
                    <td className="p-2">{r.status}</td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="border-t-2 border-slate-400 font-bold text-slate-900 bg-slate-50">
                  <td colSpan={4} className="p-2 border-r border-slate-400 text-right pr-4">
                    รวมทั้งสิ้น
                  </td>
                  <td className="p-2 border-r border-slate-400">135.00</td>
                  <td className="p-2 border-r border-slate-400">3.85%</td>
                  <td className="p-2" />
                </tr>
              </tbody>
            </table>
          </div>

          {/* Notes */}
          <div className="text-xs text-slate-700">
            <span className="font-bold">หมายเหตุ : </span>
            <span>{note || '-'}</span>
          </div>

          {/* Signature Block */}
          <div className="pt-8 flex justify-end">
            <div className="text-center space-y-2 text-xs">
              <div className="w-56 border-b border-dotted border-slate-400 mx-auto" />
              <div className="text-slate-700 font-medium">
                ({displaySigner || '...................................................'})
              </div>
              <div className="text-[11px] text-slate-500">
                {signerPosition}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
